import { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, Archive, Check, Pencil, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CitationChip, type CitationChipData } from '@/components/ui/citation-chip';
import { ErrorMessage } from '@/components/ui/error-message';
import { PostureChip, type PostureStatus } from '@/components/ui/posture-chip';
import { ProvenanceChip } from '@/components/ui/provenance-chip';
import { Spinner } from '@/components/ui/spinner';
import { CitationSourceModal } from '@/components/modals/citation-source-modal';
import {
  useArchiveCheckpointMutation,
  useAssessmentDetailQuery,
  useCheckpointAssessmentsQuery,
  useCreateCheckpointMutation,
  usePatchCheckpointMutation,
  type Checkpoint,
  type CheckpointCriterion,
  type OverallPosture,
} from '@/lib/api/checkpoints';
import { useScheduleMilestonesQuery } from '@/lib/api/health-schedule';
import { LinkedDeliverablesSection } from './linked-deliverables';
import { cn } from '@/lib/utils/cn';
import {
  checkpointLabel,
  checkpointState,
  formatDay,
  relativeDays,
  type CheckpointState,
} from './checkpoint-status';

/**
 * Right-hand drawer for one IV&V checkpoint (E06-F04).
 *
 * view   — what the checkpoint confirms, when, what IV&V assesses (with the
 *          latest round's per-criterion result), the SOW citation, and the
 *          scoring history. One primary action: score it.
 * edit   — name, purpose, when (follow a schedule milestone or a fixed
 *          date), criteria. Criteria lock once a scoring round exists
 *          (API rule `checkpoint.criteria_locked`), so they render read-only
 *          then. Archive lives here; there is no delete.
 * create — the same form for a manually added checkpoint.
 */

export type CheckpointDrawerMode =
  | { mode: 'view'; checkpointId: string }
  | { mode: 'edit'; checkpointId: string }
  | { mode: 'create' };

interface CheckpointDrawerProps {
  engagementId: string;
  checkpoints: ReadonlyArray<Checkpoint>;
  /** Append position for a manually created checkpoint. */
  nextSequence: number;
  state: CheckpointDrawerMode | null;
  onStateChange: (next: CheckpointDrawerMode | null) => void;
  onScore: (checkpoint: Checkpoint) => void;
}

export function CheckpointDrawer({
  engagementId,
  checkpoints,
  nextSequence,
  state,
  onStateChange,
  onScore,
}: CheckpointDrawerProps) {
  const checkpoint =
    state && state.mode !== 'create'
      ? (checkpoints.find((c) => c.checkpoint_id === state.checkpointId) ?? null)
      : null;
  const open = state !== null && (state.mode === 'create' || checkpoint !== null);

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onStateChange(null)}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-scrim" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[600px] flex-col bg-paper-0 shadow-modal focus:outline-none"
        >
          {state?.mode === 'view' && checkpoint ? (
            <ViewPane
              engagementId={engagementId}
              checkpoint={checkpoint}
              onEdit={() => onStateChange({ mode: 'edit', checkpointId: checkpoint.checkpoint_id })}
              onScore={() => onScore(checkpoint)}
            />
          ) : state?.mode === 'edit' && checkpoint ? (
            <EditPane
              key={checkpoint.checkpoint_id}
              engagementId={engagementId}
              checkpoint={checkpoint}
              nextSequence={nextSequence}
              onDone={() => onStateChange({ mode: 'view', checkpointId: checkpoint.checkpoint_id })}
              onArchived={() => onStateChange(null)}
            />
          ) : state?.mode === 'create' ? (
            <EditPane
              key="create"
              engagementId={engagementId}
              checkpoint={null}
              nextSequence={nextSequence}
              onDone={() => onStateChange(null)}
              onArchived={() => onStateChange(null)}
            />
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ===========================================================================
// Shared bits
// ===========================================================================

function DrawerHeader({
  kicker,
  title,
  children,
}: {
  kicker: string;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-ink-100 px-6 pb-4 pt-5">
      <div className="min-w-0">
        <div className="text-meta font-semibold uppercase tracking-wide text-ink-500">{kicker}</div>
        <Dialog.Title className="mt-1 text-h2 font-semibold text-ink-900">{title}</Dialog.Title>
        {children}
      </div>
      <Dialog.Close
        aria-label="Close"
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-card border border-ink-200 text-ink-700 hover:bg-paper-100"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </Dialog.Close>
    </div>
  );
}

function Section({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-meta font-semibold text-ink-700">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

export function CheckpointStateChip({ state }: { state: CheckpointState }) {
  if (state.posture === null) {
    return (
      <span className="inline-flex h-[22px] items-center gap-1 rounded-full bg-accent-amber-100 px-2.5 text-meta font-medium text-accent-amber-700">
        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
        {state.label}
      </span>
    );
  }
  return (
    <PostureChip status={state.posture} dot>
      {state.label}
    </PostureChip>
  );
}

export function sourceCitationChip(c: Checkpoint): CitationChipData | null {
  const s = c.source_citation;
  if (!s) return null;
  return {
    id: s.citation_id,
    doc_label: s.document_title.replace(/\.(pdf|docx?)$/i, ''),
    section: s.section_path,
    page: s.page_number,
    confidence: s.confidence,
  };
}

const ROUND_POSTURE: Record<OverallPosture, { status: PostureStatus; label: string }> = {
  green: { status: 'healthy', label: 'Met' },
  yellow_divergent: { status: 'at-risk', label: 'Conditional' },
  red: { status: 'critical', label: 'Missed' },
  grey: { status: 'unassessed', label: 'Scored' },
};

function criterionResult(scoreValue: string | number | undefined): React.ReactNode {
  if (scoreValue === undefined || scoreValue === '') return null;
  const v = String(scoreValue).toLowerCase();
  if (v === 'pass' || v === 'green') return <PostureChip status="healthy">Met</PostureChip>;
  if (v === 'fail' || v === 'red') return <PostureChip status="critical">Not met</PostureChip>;
  if (v === 'yellow') return <PostureChip status="at-risk">Partly met</PostureChip>;
  return <span className="font-mono text-mono-sm text-ink-700">{String(scoreValue)}</span>;
}

// ===========================================================================
// View
// ===========================================================================

function ViewPane({
  engagementId,
  checkpoint: c,
  onEdit,
  onScore,
}: {
  engagementId: string;
  checkpoint: Checkpoint;
  onEdit: () => void;
  onScore: () => void;
}) {
  const state = checkpointState(c);
  const rounds = useCheckpointAssessmentsQuery(c.checkpoint_id);
  const latestSubmitted = useMemo(
    () =>
      [...(rounds.data?.items ?? [])]
        .filter((r) => r.status === 'submitted')
        .sort((a, b) => b.round_seq - a.round_seq)[0],
    [rounds.data],
  );
  const detail = useAssessmentDetailQuery(latestSubmitted?.assessment_id);
  const scoreByCriterion = useMemo(
    () => new Map((detail.data?.scores ?? []).map((s) => [s.criterion_id, s.score_value])),
    [detail.data],
  );
  const [openCite, setOpenCite] = useState<CitationChipData | null>(null);
  const cite = sourceCitationChip(c);
  const label = checkpointLabel(c);
  const latest = c.latest_assessment;
  const primaryLabel =
    c.criteria.length === 0
      ? 'Add criteria to score'
      : latest?.status === 'in_progress'
        ? `Continue round ${latest.round_seq}`
        : latest
          ? `Start round ${latest.round_seq + 1}`
          : 'Start scoring';

  return (
    <>
      <DrawerHeader kicker={`${label ?? 'Unnumbered'} · IV&V checkpoint`} title={c.name}>
        <div className="mt-2 flex items-center gap-2">
          <CheckpointStateChip state={state} />
          <span className="text-meta text-ink-500">{state.detail}</span>
        </div>
      </DrawerHeader>

      <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
        <dl className="grid grid-cols-3 divide-x divide-ink-100 rounded-card border border-ink-100">
          <div className="px-4 py-3">
            <dt className="text-meta text-ink-500">Due</dt>
            <dd className="mt-0.5 font-mono text-body-sm font-medium text-ink-900">
              {c.due_date ? formatDay(c.due_date) : 'No date'}
            </dd>
            <dd className="text-meta text-ink-500">
              {c.due_date ? relativeDays(c.due_date) : 'set one in Edit'}
            </dd>
          </div>
          <div className="px-4 py-3">
            <dt className="text-meta text-ink-500">Follows</dt>
            <dd className="mt-0.5 text-body-sm font-medium text-ink-900">
              {c.milestone ? c.milestone.name : c.target_date ? 'Fixed date' : 'Nothing linked'}
            </dd>
            {c.sow_timing ? <dd className="text-meta text-ink-500">SOW: {c.sow_timing}</dd> : null}
          </div>
          <div className="px-4 py-3">
            <dt className="text-meta text-ink-500">Origin</dt>
            <dd className="mt-1">
              <ProvenanceChip
                origin={c.source === 'ai_proposed_confirmed' ? 'ai_confirmed' : 'manual'}
              />
            </dd>
          </div>
        </dl>

        <Section title="What this checkpoint confirms">
          <p className="text-body-sm text-ink-700">
            {c.purpose ?? <span className="text-ink-500">Not stated. Add a one-line purpose.</span>}
          </p>
        </Section>

        <Section
          title={`What IV&V assesses · ${c.criteria.length}`}
          aside={
            latestSubmitted ? (
              <span className="text-meta text-ink-500">Round {latestSubmitted.round_seq}</span>
            ) : null
          }
        >
          {c.criteria.length > 0 ? (
            <ol className="divide-y divide-ink-100 rounded-card border border-ink-100">
              {c.criteria.map((cr, i) => (
                <li
                  key={cr.criterion_id}
                  className="grid grid-cols-[22px_1fr_auto] items-center gap-3 px-3 py-2.5 text-body-sm text-ink-900"
                >
                  <span className="font-mono text-mono-sm text-ink-300">{i + 1}</span>
                  <span>{cr.label}</span>
                  {criterionResult(scoreByCriterion.get(cr.criterion_id))}
                </li>
              ))}
            </ol>
          ) : (
            <div className="rounded-card border border-dashed border-ink-200 bg-paper-50 px-4 py-3">
              <p className="text-body-sm font-medium text-ink-900">No criteria yet</p>
              <p className="mt-0.5 text-body-sm text-ink-700">
                The SOW names this checkpoint but does not list what it covers. Add the areas IV&V
                will assess before it can be scored.
              </p>
            </div>
          )}
        </Section>

        <Section title="Depends on these deliverables">
          <LinkedDeliverablesSection
            engagementId={engagementId}
            checkpointId={c.checkpoint_id}
            editable={c.status === 'active'}
          />
        </Section>

        <Section title="Source">
          {cite ? (
            <div className="flex items-center gap-2">
              <CitationChip cite={cite} onOpen={() => setOpenCite(cite)} />
              <span className="text-meta text-ink-500">Open to read the SOW passage</span>
            </div>
          ) : (
            <p className="text-body-sm text-ink-500">
              {c.source === 'ai_proposed_confirmed'
                ? 'Approved from an AI proposal with no stored citation.'
                : 'Added manually. No SOW citation.'}
            </p>
          )}
        </Section>

        <Section title={`Scoring history · ${c.rounds_count}`}>
          {rounds.isLoading ? (
            <div className="flex items-center gap-2 text-meta text-ink-500">
              <Spinner /> Loading rounds…
            </div>
          ) : (rounds.data?.items.length ?? 0) === 0 ? (
            <p className="text-body-sm text-ink-500">
              Not scored yet. A round records each criterion with evidence and a result.
            </p>
          ) : (
            <ul className="divide-y divide-ink-100">
              {[...rounds.data!.items]
                .sort((a, b) => b.round_seq - a.round_seq)
                .map((r) => {
                  const posture =
                    r.status === 'submitted' && r.overall_posture
                      ? ROUND_POSTURE[r.overall_posture]
                      : null;
                  return (
                    <li key={r.assessment_id} className="flex items-center gap-3 py-2.5">
                      <span className="w-16 font-mono text-mono-sm text-ink-500">
                        Round {r.round_seq}
                      </span>
                      {posture ? (
                        <PostureChip status={posture.status} dot>
                          {posture.label}
                        </PostureChip>
                      ) : (
                        <PostureChip status="unassessed">In progress</PostureChip>
                      )}
                      <span className="text-meta text-ink-500">
                        {r.submitted_at
                          ? `submitted ${formatDay(r.submitted_at.slice(0, 10))}`
                          : `started ${formatDay(r.created_at.slice(0, 10))}`}
                      </span>
                    </li>
                  );
                })}
            </ul>
          )}
          {detail.data?.findings || detail.data?.recommendations ? (
            <div className="mt-3 space-y-2 rounded-card border border-ink-100 bg-paper-50 px-4 py-3 text-body-sm text-ink-700">
              {detail.data.findings ? (
                <p>
                  <span className="font-semibold text-ink-900">
                    Round {latestSubmitted?.round_seq} findings.
                  </span>{' '}
                  {detail.data.findings}
                </p>
              ) : null}
              {detail.data.recommendations ? (
                <p>
                  <span className="font-semibold text-ink-900">Recommendation.</span>{' '}
                  {detail.data.recommendations}
                </p>
              ) : null}
            </div>
          ) : null}
        </Section>
      </div>

      <div className="flex items-center gap-2 border-t border-ink-100 bg-paper-50 px-6 py-3">
        {c.status === 'active' ? (
          <Button type="button" variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            Edit
          </Button>
        ) : null}
        <span className="flex-1" />
        {c.status === 'active' ? (
          <Button
            type="button"
            size="sm"
            className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
            onClick={c.criteria.length === 0 ? onEdit : onScore}
          >
            {c.criteria.length === 0 ? (
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {primaryLabel}
          </Button>
        ) : (
          <span className="text-meta text-ink-500">Archived — kept for audit and history.</span>
        )}
      </div>

      {openCite ? (
        <CitationSourceModal
          engagementId={engagementId}
          cite={openCite}
          open
          onOpenChange={(o) => !o && setOpenCite(null)}
        />
      ) : null}
    </>
  );
}

// ===========================================================================
// Edit / create
// ===========================================================================

type WhenMode = 'milestone' | 'date' | 'none';

interface DraftCriterion {
  criterion_id: string;
  label: string;
  score_type: CheckpointCriterion['score_type'];
  weight: number;
}

const inputCls =
  'h-9 w-full rounded-card border border-ink-200 bg-paper-0 px-3 text-body-sm text-ink-900 placeholder:text-ink-300 focus:border-ink-300 focus:outline-none focus:ring-2 focus:ring-accent-amber-600';

function EditPane({
  engagementId,
  checkpoint: c,
  nextSequence,
  onDone,
  onArchived,
}: {
  engagementId: string;
  checkpoint: Checkpoint | null;
  nextSequence: number;
  onDone: () => void;
  onArchived: () => void;
}) {
  const creating = c === null;
  const criteriaLocked = (c?.rounds_count ?? 0) > 0;
  const schedule = useScheduleMilestonesQuery(engagementId);
  const create = useCreateCheckpointMutation(engagementId);
  const patch = usePatchCheckpointMutation(engagementId);
  const archive = useArchiveCheckpointMutation(engagementId);

  const [name, setName] = useState(c?.name ?? '');
  const [purpose, setPurpose] = useState(c?.purpose ?? '');
  const [when, setWhen] = useState<WhenMode>(
    c?.schedule_milestone_id ? 'milestone' : c?.target_date ? 'date' : 'none',
  );
  const [milestoneId, setMilestoneId] = useState(c?.schedule_milestone_id ?? '');
  const [targetDate, setTargetDate] = useState(c?.target_date ?? '');
  const [criteria, setCriteria] = useState<DraftCriterion[]>(() =>
    c && c.criteria.length > 0
      ? c.criteria.map((x) => ({ ...x }))
      : [{ criterion_id: crypto.randomUUID(), label: '', score_type: 'pass_fail', weight: 1 }],
  );
  const [confirmArchive, setConfirmArchive] = useState(false);

  // Default the milestone picker to the first milestone once they load.
  useEffect(() => {
    if (when === 'milestone' && !milestoneId && schedule.milestones[0]) {
      setMilestoneId(schedule.milestones[0].registerEntryId);
    }
  }, [when, milestoneId, schedule.milestones]);

  const filledCriteria = criteria.filter((x) => x.label.trim().length > 0);
  const whenValid =
    when === 'none' ||
    (when === 'milestone' && milestoneId.length > 0) ||
    (when === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(targetDate));
  const canSave = name.trim().length > 0 && whenValid;
  const saving = create.isPending || patch.isPending;
  const error = create.error ?? patch.error ?? archive.error;

  const whenFields = {
    schedule_milestone_id: when === 'milestone' ? milestoneId : null,
    target_date: when === 'date' ? targetDate : null,
  };

  async function save() {
    if (!canSave) return;
    const trimmedPurpose = purpose.trim() || null;
    try {
      if (creating) {
        await create.mutateAsync({
          engagement_id: engagementId,
          name: name.trim(),
          sequence: nextSequence,
          purpose: trimmedPurpose,
          ...whenFields,
          criteria: filledCriteria.map(({ label, score_type, weight }) => ({
            label: label.trim(),
            score_type,
            weight,
          })),
          source: 'manually_added',
        });
      } else {
        await patch.mutateAsync({
          checkpointId: c.checkpoint_id,
          body: {
            name: name.trim(),
            purpose: trimmedPurpose,
            ...whenFields,
            ...(criteriaLocked
              ? {}
              : { criteria: filledCriteria.map((x) => ({ ...x, label: x.label.trim() })) }),
          },
        });
      }
      onDone();
    } catch {
      // Surfaced through the mutation's error state below the form.
    }
  }

  async function doArchive() {
    if (!c) return;
    try {
      await archive.mutateAsync(c.checkpoint_id);
      onArchived();
    } catch {
      // Surfaced through the mutation's error state below the form.
    }
  }

  return (
    <>
      <DrawerHeader
        kicker={creating ? 'New checkpoint' : `${checkpointLabel(c) ?? 'Unnumbered'} · Edit`}
        title={creating ? 'Add a checkpoint' : c.name}
      />

      <form
        className="flex-1 space-y-5 overflow-y-auto px-6 py-5"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <label className="block">
          <span className="mb-1.5 block text-meta font-medium text-ink-700">Name</span>
          <input
            className={inputCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Cutover dress rehearsal review"
            required
          />
        </label>

        <label className="block">
          <span className="mb-1.5 block text-meta font-medium text-ink-700">
            What this checkpoint confirms{' '}
            <span className="font-normal text-ink-500">· one sentence</span>
          </span>
          <input
            className={inputCls}
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="e.g. The cutover runbook has been rehearsed end to end"
          />
        </label>

        <fieldset>
          <legend className="mb-1.5 text-meta font-medium text-ink-700">When</legend>
          <div className="flex flex-wrap items-center gap-3">
            <div
              role="radiogroup"
              aria-label="How the due date is set"
              className="inline-flex overflow-hidden rounded-card border border-ink-200"
            >
              {(
                [
                  ['milestone', 'Follow a milestone'],
                  ['date', 'Fixed date'],
                  ['none', 'No date'],
                ] as const
              ).map(([value, text]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={when === value}
                  onClick={() => setWhen(value)}
                  className={cn(
                    'border-r border-ink-200 px-3 py-1.5 text-meta font-medium last:border-r-0',
                    when === value
                      ? 'bg-ink-900 text-paper-0'
                      : 'bg-paper-0 text-ink-700 hover:bg-paper-50',
                  )}
                >
                  {text}
                </button>
              ))}
            </div>
            {when === 'milestone' ? (
              schedule.milestones.length > 0 ? (
                <select
                  aria-label="Milestone"
                  className={cn(inputCls, 'w-72')}
                  value={milestoneId}
                  onChange={(e) => setMilestoneId(e.target.value)}
                >
                  {schedule.milestones.map((m) => (
                    <option key={m.registerEntryId} value={m.registerEntryId}>
                      {m.name}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-meta text-ink-500">
                  {schedule.isLoading ? 'Loading milestones…' : 'No schedule milestones yet.'}
                </span>
              )
            ) : when === 'date' ? (
              <input
                type="date"
                aria-label="Due date"
                className={cn(inputCls, 'w-44')}
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
              />
            ) : null}
          </div>
          <p className="mt-1.5 text-meta text-ink-500">
            {when === 'milestone'
              ? 'The due date moves when the milestone forecast moves.'
              : when === 'date'
                ? 'Use a milestone instead when one exists, so the date stays current.'
                : 'Without a date this checkpoint never shows as due or overdue.'}
            {c?.sow_timing ? ` SOW timing: ${c.sow_timing}.` : ''}
          </p>
        </fieldset>

        <fieldset>
          <legend className="mb-1.5 text-meta font-medium text-ink-700">What IV&V assesses</legend>
          {criteriaLocked ? (
            <>
              <ol className="divide-y divide-ink-100 rounded-card border border-ink-100">
                {c!.criteria.map((cr) => (
                  <li key={cr.criterion_id} className="px-3 py-2 text-body-sm text-ink-900">
                    {cr.label}
                  </li>
                ))}
              </ol>
              <p className="mt-1.5 text-meta text-ink-500">
                Criteria are locked once scoring starts so earlier rounds stay comparable. Archive
                this checkpoint and add a new one to change them.
              </p>
            </>
          ) : (
            <div className="space-y-2">
              {criteria.map((cr, i) => (
                <div key={cr.criterion_id} className="flex items-center gap-2">
                  <input
                    aria-label={`Criterion ${i + 1}`}
                    className={inputCls}
                    value={cr.label}
                    placeholder="e.g. Rollback rehearsed within the cutover window"
                    onChange={(e) =>
                      setCriteria((prev) =>
                        prev.map((x, xi) => (xi === i ? { ...x, label: e.target.value } : x)),
                      )
                    }
                  />
                  <button
                    type="button"
                    aria-label={`Remove criterion ${i + 1}`}
                    disabled={criteria.length === 1}
                    onClick={() => setCriteria((prev) => prev.filter((_, xi) => xi !== i))}
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-card border border-ink-200 text-ink-500 hover:bg-paper-100 disabled:opacity-40"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setCriteria((prev) => [
                    ...prev,
                    {
                      criterion_id: crypto.randomUUID(),
                      label: '',
                      score_type: 'pass_fail',
                      weight: 1,
                    },
                  ])
                }
                className="inline-flex items-center gap-1 text-meta font-medium text-ink-700 hover:underline"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                Add criterion
              </button>
              <p className="text-meta text-ink-500">
                New criteria are scored met or not met, with equal weight.
              </p>
            </div>
          )}
        </fieldset>

        {error ? <ErrorMessage error={error} /> : null}
        {/* Enter in any input submits. */}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>

      <div className="flex items-center gap-2 border-t border-ink-100 bg-paper-50 px-6 py-3">
        {!creating ? (
          confirmArchive ? (
            <div className="flex items-center gap-2">
              <span className="text-meta text-ink-700">Archive this checkpoint?</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-posture-critical text-posture-critical hover:bg-posture-critical-tint"
                disabled={archive.isPending}
                onClick={() => void doArchive()}
              >
                {archive.isPending ? <Spinner /> : null}
                Archive
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setConfirmArchive(false)}
              >
                Keep
              </Button>
            </div>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmArchive(true)}>
              <Archive className="h-3.5 w-3.5" aria-hidden="true" />
              Archive
            </Button>
          )
        ) : null}
        <span className="flex-1" />
        <Button type="button" variant="outline" size="sm" onClick={onDone}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={!canSave || saving}
          onClick={() => void save()}
          className="bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700"
        >
          {saving ? <Spinner /> : <Check className="h-3.5 w-3.5" aria-hidden="true" />}
          {creating ? 'Add checkpoint' : 'Save'}
        </Button>
      </div>
    </>
  );
}
